import React, { useState } from "react";
import { Table, Button, Card, Form, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  useGetBlogCategoriesQuery,
  useCreateBlogCategoryMutation,
  useUpdateBlogCategoryMutation,
  useDeleteBlogCategoryMutation,
  useToggleBlogCategoryStatusMutation,
} from "../../../redux/api/BlogCategoriesApi/blogCategoriesApi";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import {
  FaPlus,
  FaEdit,
  FaArrowLeft,
  FaTag,
  FaCheckCircle,
  FaTimesCircle,
  FaTrash,
} from "react-icons/fa";
import { message } from "antd";
import Swal from "sweetalert2";

const BlogCategories = () => {
  const navigate = useNavigate();
  const { data: categories, isLoading } = useGetBlogCategoriesQuery();
  const [createCategory] = useCreateBlogCategoryMutation();
  const [updateCategory] = useUpdateBlogCategoryMutation();
  const [deleteCategory] = useDeleteBlogCategoryMutation();
  const [toggleStatus] = useToggleBlogCategoryStatusMutation();

  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    status: true,
  });

  const handleOpenModal = (cat = null) => {
    if (cat) {
      setEditingCategory(cat);
      setFormData({ name: cat.name, slug: cat.slug, status: cat.status });
    } else {
      setEditingCategory(null);
      setFormData({ name: "", slug: "", status: true });
    }
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };
      if (name === "name" && !editingCategory) {
        newData.slug = value
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");
      }
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await updateCategory({
          id: editingCategory.id,
          data: formData,
        }).unwrap();
        message.success("Category updated");
      } else {
        await createCategory(formData).unwrap();
        message.success("Category created");
      }
      setShowModal(false);
    } catch (err) {
      message.error(err.data?.error || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Blogs in this category will become uncategorized.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await deleteCategory(id).unwrap();
        message.success("Category deleted");
      } catch (err) {
        message.error("Failed to delete category");
      }
    }
  };

  const handleToggle = async (id) => {
    try {
      await toggleStatus(id).unwrap();
      message.success("Status updated");
    } catch (err) {
      message.error("Failed to update status");
    }
  };

  if (isLoading)
    return (
      <div className="d-flex justify-content-center py-5">
        <LoaderSpinner />
      </div>
    );

  return (
    <div className="container py-4 fade-in">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-3">
          <Button
            variant="light"
            onClick={() => navigate("/admin/blogs")}
            className="rounded-circle shadow-sm p-2"
          >
            <FaArrowLeft />
          </Button>
          <div>
            <h2 className="fw-bold text-dark mb-1">
              <FaTag className="text-primary me-2" size={24} />
              Blog Categories
            </h2>
            <p className="text-muted mb-0">
              Organize your blog posts into categories
            </p>
          </div>
        </div>
        <Button
          variant="primary"
          onClick={() => handleOpenModal()}
          className="d-flex align-items-center gap-2 px-4 shadow-sm rounded-pill fw-semibold"
        >
          <FaPlus /> Add New Category
        </Button>
      </div>

      <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
        <div className="table-responsive">
          <Table hover className="align-middle mb-0 custom-table">
            <thead className="bg-light text-muted small text-uppercase fw-bold">
              <tr>
                <th className="ps-4">Category Name</th>
                <th>Slug</th>
                <th className="text-center">Status</th>
                <th className="text-end pe-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories?.map((cat) => (
                <tr key={cat.id}>
                  <td className="ps-4 fw-bold text-dark">{cat.name}</td>
                  <td>
                    <code>{cat.slug}</code>
                  </td>
                  <td className="text-center">
                    <Button
                      variant="link"
                      className={`text-decoration-none ${cat.status ? "text-success" : "text-danger"}`}
                      onClick={() => handleToggle(cat.id)}
                    >
                      {cat.status ? (
                        <FaCheckCircle className="me-1" />
                      ) : (
                        <FaTimesCircle className="me-1" />
                      )}
                      {cat.status ? "Active" : "Inactive"}
                    </Button>
                  </td>
                  <td className="text-end pe-4">
                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        variant="white"
                        size="sm"
                        className="btn-action shadow-sm border"
                        onClick={() => handleOpenModal(cat)}
                      >
                        <FaEdit className="text-primary" />
                      </Button>
                      <Button
                        variant="white"
                        size="sm"
                        className="btn-action shadow-sm border"
                        onClick={() => handleDelete(cat.id)}
                      >
                        <FaTrash className="text-danger" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editingCategory ? "Edit Category" : "Add New Category"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Category Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g. Technology"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Slug</Form.Label>
              <Form.Control
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Check
                type="switch"
                label="Active"
                name="status"
                checked={formData.status}
                onChange={handleChange}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingCategory ? "Update Category" : "Create Category"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default BlogCategories;
